import type * as React from 'react';

import { Popover, PopoverContent, PopoverTrigger } from '../Popover';
import { useBadgePopover } from './useBadgePopover';

type CardName =
  | {
      /** Names the card when it has no visible title. */
      cardLabel: string;
      cardLabelledBy?: never;
    }
  | {
      /** Id of the element inside `content` that names the card, such as its title. */
      cardLabelledBy: string;
      cardLabel?: never;
    };

export type BadgePopoverProps = CardName & {
  /** One native button, or an element that renders one and passes props and ref through. */
  children: React.ReactElement;
  /** The card. It brings its own surface: background, padding and shadow. */
  content: React.ReactNode;
  /** Id of the element inside `content` that describes the card. */
  cardDescribedBy?: string;
};

/**
 * Opens a card from a badge, or from one button around a group of badges. Hover opens it without
 * moving focus; click, Enter or Space open it with focus on the card. Tab walks the card's links and
 * out, Escape returns focus to the trigger, and activating a link or button in the card closes it.
 * The trigger gets a focus ring, start-aligned text in place of a button's centering, and a pointer
 * target at least 24×24 px.
 */
function BadgePopover({ children, content, cardLabel, cardLabelledBy, cardDescribedBy }: BadgePopoverProps) {
  const { isOpen, onOpenChange, triggerProps, contentProps } = useBadgePopover({
    label: cardLabel,
    labelledBy: cardLabelledBy,
    describedBy: cardDescribedBy,
  });

  return (
    <Popover open={isOpen} onOpenChange={onOpenChange}>
      <PopoverTrigger
        asChild
        className={
          // The centred ::after keeps the pointer target at least 24×24 px around a small badge.
          "relative cursor-pointer text-start focus-ring after:absolute after:top-1/2 after:left-1/2 after:h-6 after:w-full after:min-w-6 after:-translate-x-1/2 after:-translate-y-1/2 after:content-['']"
        }
        {...triggerProps}
      >
        {children}
      </PopoverTrigger>
      <PopoverContent
        {...contentProps}
        side="bottom"
        sideOffset={12}
        className="pointer-events-auto border-0 bg-transparent p-0 shadow-none"
      >
        {content}
      </PopoverContent>
    </Popover>
  );
}

export { BadgePopover };
